import React, { useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import { Bed, ChevronLeft, ChevronRight } from 'lucide-react';
import BedroomDialog, { BedroomData } from '@/components/BedroomDialog';
import { useBedrooms } from '@/hooks/useBedrooms';

const BedroomCarousel = () => {
  const bedrooms = useBedrooms();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedBedroom, setSelectedBedroom] = useState<BedroomData | null>(null);

  const openBedroomDetails = (bedroom: BedroomData) => {
    setSelectedBedroom(bedroom);
    setIsDialogOpen(true);
  };

  return (
    <div className="bg-white rounded-none sm:rounded-lg py-4 sm:p-6 shadow-none sm:shadow-md w-full">
      <h2 className="text-xl sm:text-2xl font-semibold mb-4 sm:mb-6">Où vous dormirez</h2>
      
      <div className="relative group">
        <Swiper 
          modules={[Pagination, Navigation]} 
          spaceBetween={16} 
          slidesPerView={1.2} 
          pagination={{
            clickable: true
          }}
          navigation={{
            prevEl: '.swiper-button-prev-bedrooms',
            nextEl: '.swiper-button-next-bedrooms',
          }}
          breakpoints={{
            640: {
              slidesPerView: 2.2,
              spaceBetween: 20
            },
            768: {
              slidesPerView: 3,
              spaceBetween: 24
            }
          }} 
          className="!pb-10"
        >
          {bedrooms.map(bedroom => 
            <SwiperSlide key={bedroom.id}>
              <button 
                className="w-full text-left bg-white rounded-lg overflow-hidden border border-gray-200 shadow-md transition-transform hover:scale-[1.02]" 
                onClick={() => openBedroomDetails(bedroom)} 
                aria-label={`Voir les détails de ${bedroom.name}`}
              >
                <div className="relative h-48 sm:h-56">
                  <img 
                    src={bedroom.images[0]} 
                    alt={`Photo de ${bedroom.name}`} 
                    className="w-full h-full object-cover" 
                    loading="lazy"
                    decoding="async"
                    onError={(e) => {
                      e.currentTarget.src = "https://placehold.co/600x400?text=Chambre";
                    }} 
                  />
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Bed className="w-5 h-5 text-gray-600" />
                    <h3 className="font-medium">{bedroom.name}</h3>
                  </div>
                  <p className="text-gray-600">{bedroom.beds}</p>
                </div>
              </button>
            </SwiperSlide>
          )}
        </Swiper>
        
        {/* Navigation Arrows */}
        <button 
          className="swiper-button-prev-bedrooms hidden md:flex absolute top-1/2 -translate-y-1/2 left-4 z-10 p-2 bg-white bg-opacity-70 hover:bg-opacity-100 rounded-full shadow transition-all duration-200 items-center justify-center group-hover:opacity-100 opacity-0"
          aria-label="Chambre précédente"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        
        <button 
          className="swiper-button-next-bedrooms hidden md:flex absolute top-1/2 -translate-y-1/2 right-4 z-10 p-2 bg-white bg-opacity-70 hover:bg-opacity-100 rounded-full shadow transition-all duration-200 items-center justify-center group-hover:opacity-100 opacity-0"
          aria-label="Chambre suivante"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>

      <BedroomDialog 
        isOpen={isDialogOpen} 
        onOpenChange={setIsDialogOpen} 
        bedroom={selectedBedroom} 
      />
    </div>
  );
};

export default BedroomCarousel;